import { useContext } from 'react';
import { AuthContext } from './AuthContext';
import React, { useState, useEffect } from 'react';
import { fetchWithAuth, authenticatedFetch, API_ENDPOINT } from './utils/api';
import { FileText, Upload, BarChart3, LogOut, Activity, Users, X, Shield, ShieldAlert, Menu, ClipboardList } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import logoSideWhite from './assets/hazdex-side-orange-wht.png';
import { NavItem, LoadingState } from './Components/Common';
import HomeView from './Components/HomeView';
import AnalyticsView from './Components/AnalyticsView';
import ReportsView from './Components/ReportsView';
import UploadView from './Components/UploadView';
import HistoricDataManagement from './Components/HistoricDataManagement';
import CorrectiveActionsView from './Components/CorrectiveActionsView';
import THIRAView from './Components/THIRAView';
import UserManagementView from './Components/UserManagementView';
import SuperAdminView from './Components/SuperAdminView';
import EventDetailsView from './Components/EventDetailsView';
import { PublicFeedbackForm } from './Components/PublicFeedbackForm';
import { OrgUsageBillingView } from './Components/UsageMonitor';
import PublicCAUpdate from './Components/PublicCAUpdate';

function App() {
  const { organization, user, logout } = useContext(AuthContext);
  const isAdmin = organization?.role === 'admin';
  const isSuperAdmin = organization?.organizationId === 'hazdex-superadmin'; 
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeView, setActiveView] = useState('home');
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedHistoricReport, setSelectedHistoricReport] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topWeakCapabilities, setTopWeakCapabilities] = useState([]);
  const [topStrengths, setTopStrengths] = useState([]);

  // Filter reports
  const regularReports = reports.filter(report => !report.is_historic);
  const historicReports = reports.filter(report => report.is_historic);

  // Set initial view based on user type
  useEffect(() => {
    if (organization?.organizationId) {
      if (isSuperAdmin) {
        setActiveView('superadmin');
      } else {
        setActiveView('home');
      }
    }
  }, [organization?.organizationId, isSuperAdmin]);

  // Load dashboard data only for non-super-admin users
  useEffect(() => {
    if (organization?.organizationId) {
      if (organization.organizationId === 'hazdex-superadmin') {
        setLoading(false);
      } else {
        loadDashboardData();
      }
    } else {
      const timeout = setTimeout(() => {
        if (!organization?.organizationId) {
          toast.error('Unable to load organization. Please try logging out and back in.');
          setLoading(false);
        }
      }, 5000);
      
      return () => clearTimeout(timeout);
    }
  }, [organization?.organizationId]);

  const handleCancelProcessing = async (aarId) => {
    if (!window.confirm('Cancel AAR generation? This will stop the AI analysis in progress.')) {
      return;
    }

    setCancelling(aarId);
    const cancelToast = toast.loading('Cancelling processing...');
    
    try {
      const data = await authenticatedFetch(
        `${API_ENDPOINT}/dashboard/reports/${aarId}/cancel?organization_id=${organization?.organizationId}`,
        { method: 'POST' }
      );

      toast.success('Processing cancelled successfully', { id: cancelToast });
      await loadDashboardData();
    } catch (error) {
      console.error('Error cancelling processing:', error);
      toast.error(`Error cancelling processing: ${error.message}`, { id: cancelToast });
    } finally {
      setCancelling(null);
    }
  };

  const loadDashboardData = async () => {
    if (!organization?.organizationId || organization.organizationId === 'hazdex-superadmin') {
      return;
    }

    setLoading(true);
    try {
      const [overviewRes, trendsRes, gapsRes, reportsRes, strengthsRes] = await Promise.all([
        fetchWithAuth('/dashboard/overview', organization.organizationId),
        fetchWithAuth('/dashboard/trends?type=incident_type&range=12', organization.organizationId),
        fetchWithAuth('/dashboard/gaps', organization.organizationId),
        fetchWithAuth('/dashboard/reports', organization.organizationId),
        fetchWithAuth('/dashboard/strengths', organization.organizationId),
      ]);

      setOverview(overviewRes);
      setTrends(trendsRes);
      setGaps(gapsRes);
      setReports(reportsRes.reports || []);

      if (gapsRes && gapsRes.top_gaps) {
        setTopWeakCapabilities(gapsRes.top_gaps.slice(0, 5)); // ✅ top 5
      }

      if (strengthsRes && strengthsRes.top_strengths) {
        setTopStrengths(strengthsRes.top_strengths.slice(0, 5).map(s => ({
        category: s.nims_capability || 'General',
        description: s.strength_description,
        count: s.frequency,
        })));
      }

    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />

      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-800 text-white transition-all duration-300 flex flex-col`}>
        {/* Logo */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          {sidebarOpen && <img 
            src={logoSideWhite}
            alt="HAZDEX" 
            style={{ height: '80px', width: 'auto' }}
          />}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-slate-700 rounded">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {/* Regular users see these */}
          {!isSuperAdmin && (
            <>
              <NavItem 
                icon={<Activity className="w-5 h-5" />} 
                label="Home" 
                active={activeView === 'home'}
                onClick={() => setActiveView('home')}
                collapsed={!sidebarOpen}
              />
              <NavItem 
                icon={<Upload className="w-5 h-5" />} 
                label="New Event" 
                active={activeView === 'upload'}
                onClick={() => setActiveView('upload')}
                collapsed={!sidebarOpen}
              />
              <NavItem 
                icon={<FileText className="w-5 h-5" />} 
                label="All Events" 
                active={activeView === 'reports'}
                onClick={() => setActiveView('reports')}
                collapsed={!sidebarOpen}
              />
              <NavItem 
                icon={<BarChart3 className="w-5 h-5" />} 
                label="Analytics" 
                active={activeView === 'analytics'}
                onClick={() => setActiveView('analytics')}
                collapsed={!sidebarOpen}
              />
              <NavItem 
                icon={<ClipboardList className="w-5 h-5" />} 
                label="Corrective Actions" 
                active={activeView === 'corrective-actions'}
                onClick={() => setActiveView('corrective-actions')}
                collapsed={!sidebarOpen}
              />
              <NavItem
                icon={<ShieldAlert className="w-5 h-5" />}
                label="THIRA / SPR"
                active={activeView === 'thira'}
                onClick={() => setActiveView('thira')}
                collapsed={!sidebarOpen}
              />
              <NavItem 
                icon={<FileText className="w-5 h-5" />} 
                label="Historic Data" 
                active={activeView === 'historic'}
                onClick={() => setActiveView('historic')}
                collapsed={!sidebarOpen}
              />
              
              {/* Regular Admin User Management */}
              {isAdmin && (
                <NavItem 
                  icon={<Users className="w-5 h-5" />}
                  label="User Management"
                  active={activeView === 'users'}
                  onClick={() => setActiveView('users')}
                  collapsed={!sidebarOpen}
                />
              )}

              {isAdmin && (
                <NavItem
                  icon={<BarChart3 className="w-5 h-5" />}
                  label="Usage & Billing"
                  active={activeView === 'usage'}
                  onClick={() => setActiveView('usage')}
                  collapsed={!sidebarOpen}
                />
              )}
            </>
          )}
          
          {/* Super Admin sees only this */}
          {isSuperAdmin && (
            <NavItem 
              icon={<Shield className="w-5 h-5" />}
              label="Super Admin"
              active={activeView === 'superadmin'}
              onClick={() => setActiveView('superadmin')}
              collapsed={!sidebarOpen}
            />
          )}
        </nav>

        {/* User Info with Logout */}
        <div className="p-4 border-t border-slate-700 flex-shrink-0">
          {sidebarOpen ? (
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center font-bold text-lg">
                  {user?.email?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.email || 'User Account'}</p>
                  <p className="text-xs text-gray-400 truncate">
                    {organization?.organizationName || organization?.email || 'Loading...'}
                  </p>
                  {organization?.role && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-blue-600 text-xs rounded">
                      {organization.role}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={logout}
                className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition text-sm font-medium"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          ) : (
            <button
              onClick={logout}
              className="w-full p-2 hover:bg-slate-700 rounded flex items-center justify-center"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-800">
              {activeView === 'home' && 'Dashboard Overview'}
              {activeView === 'upload' && 'Create New Event'}
              {activeView === 'reports' && 'All Events'}
              {activeView === 'analytics' && 'Analytics Dashboard'}
              {activeView === 'corrective-actions' && 'Corrective Actions'}
              {activeView === 'thira' && <THIRAView />}
              {activeView === 'historic' && 'Historic Data Management'}
              {activeView === 'users' && 'User Management'}
              {activeView === 'usage' && 'Usage & Billing'} 
              {activeView === 'superadmin' && 'Super Admin Dashboard'}
            </h2>
          </div>
          {/* Only show refresh button for regular users */}
          {!isSuperAdmin && (
            <div className="flex items-center space-x-4">
              <button 
                onClick={loadDashboardData}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
              >
                <Activity className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            </div>
          )}
        </header>

        {/* Content Area */}
        <div className="p-6">
          {loading ? (
            <LoadingState />
          ) : (
            <>
              {activeView === 'event-details' && selectedEventId && (
                <EventDetailsView 
                  eventId={selectedEventId} 
                  onBack={() => {
                    setActiveView('reports');
                    setSelectedEventId(null);
                  }}
                  onUpdate={loadDashboardData}
                />
              )}
              {activeView === 'home' && (
                <HomeView 
                  overview={overview} 
                  trends={trends} 
                  reports={reports} 
                  regularReports={regularReports} 
                  topWeakCapabilities={topWeakCapabilities} 
                  topStrengths={topStrengths} 
                  onNavigate={setActiveView}
                  onEventClick={(eventId) => {  // ✅ added
                    setSelectedEventId(eventId);
                    setActiveView('event-details');
                  }}
                />
              )}
              {activeView === 'analytics' && (
                <AnalyticsView 
                  trends={trends} 
                  gaps={gaps} 
                  reports={reports} 
                  onNavigate={setActiveView} 
                />
              )}
              {activeView === 'reports' && (
                <ReportsView 
                  reports={regularReports} 
                  onReportsChange={loadDashboardData} 
                  onEventClick={(eventId) => {
                    setSelectedEventId(eventId);
                    setActiveView('event-details');
                  }}
                  onNavigateToNewEvent={() => setActiveView('upload')}
                  cancelling={cancelling}
                  onCancelProcessing={handleCancelProcessing}
                />
              )}
              {activeView === 'upload' && (
                <UploadView 
                  onUploadComplete={(aarId) => {
                    if (aarId) {
                      setSelectedEventId(aarId);
                      setActiveView('event-details');
                    } else {
                      setActiveView('reports');
                    }
                    loadDashboardData();
                  }} 
                />
              )}
              {activeView === 'historic' && (
                <HistoricDataManagement 
                  onUploadComplete={loadDashboardData} 
                  reports={historicReports} 
                />
              )}
              {activeView === 'corrective-actions' && (
                <CorrectiveActionsView onNavigate={setActiveView} />
              )}
              {activeView === 'users' && <UserManagementView />}
              {activeView === 'usage' && <OrgUsageBillingView />}
              {activeView === 'superadmin' && <SuperAdminView />}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export { PublicFeedbackForm };
export default App;